import { InteractiveMapLocation } from "@redotech/redo-common-model-location-common/interactive-map-location";
import {
  getWalletPlatform,
  WalletPlatform,
} from "@redotech/redo-customer-portal-shared/utils/wallet-pass";
import { useTranslation } from "@redotech/redo-i18n";
import HappyReturnsLogo from "@redotech/redo-icons/happy-returns-logo.svg";
import type {
  ReturnDropoff,
  ReturnDropoffQrCode,
} from "@redotech/redo-return-core-common/return";
import { HAPPY_RETURNS_PROVIDER } from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import {
  DropoffLocationsButton,
  DropoffLocationsSearchParams,
} from "./dropoff-locations-button";
import * as styles from "./happy-returns-confirmation-card.module.css";
import { HappyReturnsWalletButton } from "./happy-returns-wallet-button";
import { useDownloadAppleWalletPass } from "./use-download-apple-wallet-pass";

type HappyReturnsQrCode = Extract<
  ReturnDropoffQrCode,
  { provider: typeof HAPPY_RETURNS_PROVIDER }
>;

export interface HappyReturnsConfirmationCardProps {
  autoOpen?: boolean;
  customerCountry?: string;
  dropoffs: ReturnDropoff;
  modalSubtitle?: string;
  modalTitle?: string;
  onOpenUrl(url: string): void;
  returnId: string;
  searchDropoffLocations(
    params: DropoffLocationsSearchParams,
  ): Promise<InteractiveMapLocation[]>;
}

export const HappyReturnsConfirmationCard = memo(
  function HappyReturnsConfirmationCard({
    autoOpen,
    customerCountry,
    dropoffs,
    modalSubtitle,
    modalTitle,
    onOpenUrl,
    returnId,
    searchDropoffLocations,
  }: HappyReturnsConfirmationCardProps) {
    const { t } = useTranslation();
    const downloadAppleWalletPassMutation = useDownloadAppleWalletPass();
    const walletPlatform = getWalletPlatform(
      typeof navigator === "undefined" ? undefined : navigator.userAgent,
    );
    const qrCodes = dropoffs.qrCodes.filter(
      (qrCode): qrCode is HappyReturnsQrCode =>
        qrCode.provider === HAPPY_RETURNS_PROVIDER,
    );

    return (
      <Flex flexDirection="column" gap="sm" pt="sm">
        <DropoffLocationsButton
          autoOpen={autoOpen}
          customerCountry={customerCountry}
          modalSubtitle={modalSubtitle}
          modalTitle={modalTitle}
          returnId={returnId}
          searchDropoffLocations={searchDropoffLocations}
          text={t("{{provider}} locations", { provider: "Happy Returns®" })}
        />
        {qrCodes.map((qrCode) => {
          const appleWalletPassUrl =
            qrCode.providerMetadata?.appleWalletPassUrl;
          const googleWalletPassUrl =
            qrCode.providerMetadata?.googleWalletPassUrl;

          return (
            <Flex flexDirection="column" gap="sm" key={qrCode.shipmentGroupId}>
              <Flex
                borderColor="secondary"
                borderRadius="lg"
                borderStyle="solid"
                borderWidth="1px"
                flexDirection="column"
                overflow="hidden"
              >
                <Flex
                  align="center"
                  className={styles.brandHeader}
                  gap="xxs"
                  justify="center"
                  py="xxs"
                >
                  <HappyReturnsLogo height={16} width={16} />
                  <Text fontSize="sm" fontWeight="medium">
                    Happy Returns
                  </Text>
                </Flex>
                {!!qrCode.qrCodeUrl && (
                  <Flex align="center" justify="center" p="lg">
                    <img
                      alt={t("{{provider}} return QR code", {
                        provider: "Happy Returns",
                      })}
                      height={160}
                      src={qrCode.qrCodeUrl}
                      width={160}
                    />
                  </Flex>
                )}
                {walletPlatform === WalletPlatform.APPLE &&
                  appleWalletPassUrl && (
                    <Flex align="center" justify="center" pb="md">
                      <HappyReturnsWalletButton
                        onClick={() =>
                          downloadAppleWalletPassMutation.mutate(
                            appleWalletPassUrl,
                          )
                        }
                        pending={downloadAppleWalletPassMutation.isPending}
                        platform={WalletPlatform.APPLE}
                      />
                    </Flex>
                  )}
                {walletPlatform === WalletPlatform.GOOGLE &&
                  googleWalletPassUrl && (
                    <Flex align="center" justify="center" pb="md">
                      <HappyReturnsWalletButton
                        onClick={() => onOpenUrl(googleWalletPassUrl)}
                        platform={WalletPlatform.GOOGLE}
                      />
                    </Flex>
                  )}
              </Flex>

              {!!qrCode.qrCode && (
                <Flex
                  bgColor="secondary"
                  borderColor="secondary"
                  borderRadius="lg"
                  borderStyle="solid"
                  borderWidth="1px"
                  flexDirection="column"
                  gap="xs"
                  p="md"
                >
                  <Text fontSize="sm">
                    {t("Can't scan the QR code? Use the express code instead.")}
                  </Text>
                  <Flex align="center" gap="xxs">
                    <Text fontSize="sm">{t("Express code:")}</Text>
                    <Text fontSize="sm" fontWeight="semibold">
                      {qrCode.qrCode}
                    </Text>
                  </Flex>
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>
    );
  },
);
